import type React from 'react'
import styled from 'styled-components'

import { applyOpacity } from '@helpers/styled-helpers'

import { FormControlLabel } from '@components/ui/FormElements/FormControlLabel'

export const RADIO_CSS_VARIABLES = {
  color: '--radio-outer-color',
  fontSize: '--radio-outer-font-size',
  padding: '--radio-outer-padding',
}

const RadioWrapper = styled.span`
  --color: var(${RADIO_CSS_VARIABLES.color}, ${({ theme }) => applyOpacity(theme.colors.gray[100], 54)});
  --padding: var(${RADIO_CSS_VARIABLES.padding}, 9px);
  --font-size: var(${RADIO_CSS_VARIABLES.fontSize}, calc(20rem / 14));

  align-items: center;
  border-radius: 50%;
  border: 0;
  color: var(--color);
  cursor: pointer;
  display: inline-flex;
  justify-content: center;
  margin: 0;
  outline: 0;
  padding: var(--padding);
  position: relative;
  text-align: center;
  text-decoration: none;
  transition: background-color 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
  user-select: none;
  vertical-align: middle;
  -webkit-appearance: none;
  -webkit-tap-highlight-color: transparent;

  input {
    top: 0;
    left: 0;
    width: 100%;
    cursor: inherit;
    height: 100%;
    margin: 0;
    opacity: 0;
    padding: 0;
    z-index: 1;
    position: absolute;
  }

  &:hover {
    background-color: ${({ theme }) => applyOpacity(theme.colors.primary[50], 4)};
  }

  &:has([data-checked='true']),
  &:has(:focus-within) {
    --color: ${({ theme }) => theme.colors.primary[50]};
  }

  &[aria-disabled='true'] {
    --color: ${({ theme }) => applyOpacity(theme.colors.gray[100], 26)};

    cursor: default;
    pointer-events: none;
  }

  &[data-size='small'] {
    --font-size: 18px;
    --padding: var(${RADIO_CSS_VARIABLES.padding}, 7px);
  }
`

const Base = styled.span`
  width: 100%;
  display: flex;
  align-items: inherit;
  justify-content: inherit;
`

const IconWrapper = styled.div`
  --fill-transition: fill 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
  --transform-cubic-bezier: cubic-bezier(0.4, 0, 1, 1);
  --transform-transition: transform 150ms var(--transform-cubic-bezier) 0ms;

  display: flex;
  position: relative;

  svg {
    fill: var(--color);
    width: 1em;
    height: 1em;
    display: inline-block;
    font-size: var(--font-size);
    flex-shrink: 0;
    user-select: none;
    transition: var(--fill-transition), var(--transform-transition);
  }

  svg:last-child {
    left: 0;
    position: absolute;
    transform: scale(0);
  }

  &:has(+ input[data-checked='true']) svg:last-child {
    transform: scale(1);
    --transform-cubic-bezier: cubic-bezier(0, 0, 0.2, 1);
  }
`

function Svg(props: React.SVGProps<SVGSVGElement>) {
  return <svg focusable="false" viewBox="0 0 24 24" aria-hidden="true" {...props} />
}

// Radio outer circle (ring/border)
function RadioOuterCircle() {
  return (
    <Svg>
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z" />
    </Svg>
  )
}

// Radio inner filled circle (appears when checked)
function RadioInnerCircle() {
  return (
    <Svg>
      <path d="M8.465 8.465C9.37 7.56 10.62 7 12 7C14.76 7 17 9.24 17 12C17 13.38 16.44 14.63 15.535 15.535C14.63 16.44 13.38 17 12 17C9.24 17 7 14.76 7 12C7 10.62 7.56 9.37 8.465 8.465Z" />
    </Svg>
  )
}

interface RadioProps extends Omit<React.HTMLAttributes<HTMLInputElement>, 'onChange'> {
  checked?: boolean
  className?: string
  disabled?: boolean
  onChange?: (event: React.ChangeEvent<HTMLInputElement>, value: string) => void
  value?: string
  size?: 'small'
  withoutPadding?: boolean
}

export function Radio({
  className,
  value,
  checked,
  disabled,
  onChange,
  size,
  withoutPadding = false,
  ...rest
}: RadioProps) {
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange?.(event, event.target.value)
  }

  return (
    <RadioWrapper
      aria-disabled={disabled}
      className={className}
      data-size={size}
      {...(disabled && { tabIndex: -1 })}
      {...(withoutPadding && {
        style: {
          [RADIO_CSS_VARIABLES.padding]: 0,
        },
      })}
    >
      <Base>
        <IconWrapper>
          <RadioOuterCircle />
          <RadioInnerCircle />
        </IconWrapper>
        <input
          data-checked={checked}
          className="PrivateSwitchBase-input-49"
          disabled={disabled}
          onChange={handleChange}
          type="radio"
          value={value}
          // If onChange is provided, the component is controlled, otherwise it's uncontrolled
          {...(onChange != null ? { checked } : { defaultChecked: checked })}
          {...rest}
        />
      </Base>
    </RadioWrapper>
  )
}

const RadioGroupWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  flex-direction: column;
`

interface RadioOption {
  disabled?: boolean
  key: string
  label: StringOrMessage
}

interface RadioGroupProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
  disabled?: boolean
  name?: string
  onChange: (event: React.ChangeEvent<HTMLInputElement>, value: string) => void
  options: RadioOption[]
  value: string
  withoutPadding?: boolean
}

export function RadioGroup({
  disabled = false,
  name,
  onChange,
  options,
  value,
  withoutPadding = false,
  ...rest
}: RadioGroupProps) {
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(event, event.target.value)
  }

  return (
    <RadioGroupWrapper
      {...rest}
      role="radiogroup"
      aria-disabled={disabled}
      {...(withoutPadding && {
        style: {
          [RADIO_CSS_VARIABLES.padding]: 0,
        },
      })}
    >
      {options.map(option => (
        <FormControlLabel
          checked={value === option.key}
          control={<Radio />}
          disabled={disabled || option.disabled}
          key={option.key}
          label={option.label}
          name={name}
          onChange={handleChange}
          value={option.key}
          {...(withoutPadding && { gap: 10 })}
        />
      ))}
    </RadioGroupWrapper>
  )
}
